import { Box, Flex } from "grid-styled";
import { Fragment, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import Media from "react-media";
import system from "system-components";

import AcknowledgmentsPanel from "pstat-anywhere/components/acknowledgments/AcknowledgmentsPanel";
import OptionalApproval from "pstat-anywhere/components/document_control/approve_policy/OptionalApproval";
import { isOptionalApproval } from "pstat-anywhere/components/document_control/approve_policy/utils";
import ShowChangesLoadingModal from "pstat-anywhere/components/document_control/ShowChangesLoadingModal";
import ApplicabilityPanel from "pstat-anywhere/components/document_control/view_policy/applicability/ApplicabilityPanel";
import ApprovalSignatureSection from "pstat-anywhere/components/document_control/view_policy/approval_signature";
import AttachmentsPanel from "pstat-anywhere/components/document_control/view_policy/attachments/Attachments";
import CommentPanel from "pstat-anywhere/components/document_control/view_policy/comment_form";
import DocumentApprovedMessage from "pstat-anywhere/components/document_control/view_policy/DocumentApprovedMessage";
import HistoryCard from "pstat-anywhere/components/document_control/view_policy/history";
import PolicyContent from "pstat-anywhere/components/document_control/view_policy/PolicyContent";
import PolicyProperties from "pstat-anywhere/components/document_control/view_policy/PolicyProperties";
import SecondaryNav from "pstat-anywhere/components/document_control/view_policy/secondarynav/SecondaryNav";
import StandardsAndRegulationsPanel from "pstat-anywhere/components/document_control/view_policy/standards_and_regulations/StandardsAndRegulationsPanel";
import StatusBar from "pstat-anywhere/components/document_control/view_policy/StatusBar";
import TableOfContents from "pstat-anywhere/components/document_control/view_policy/table_of_contents";
import { DocumentTimeline } from "pstat-anywhere/components/document_timeline";
import LippincottSearchPanel from "pstat-anywhere/components/integrations/lippincott_search/LippincottSearchPanel";
import { ContentContainer } from "pstat-anywhere/components/pages";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import { withTenantContext } from "pstat-anywhere/context_providers/TenantContext";
import { withViewer } from "pstat-anywhere/context_providers/ViewerContext";
import theme from "pstat-anywhere/themes/policystat/theme";
import { isActive, isScheduled } from "pstat-anywhere/utils/document";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import useTaskResult from "pstat-anywhere/utils/useTaskResult";
import {
  createErrorMessage,
  createInfoMessage,
  createSuccessMessage
} from "pstat-design-system/message/manageMessages";
import { Button } from "pstat-design-system/shared/Button";
import { Text } from "pstat-design-system/text";

const ViewPolicyStyledContainer = system({
  bg: "nav.95"
}).extend`
  & sub,
  & sup {
    font-size: smaller;
  }
  & sup {
    vertical-align: super;
  }
  & sub {
    vertical-align: sub;
  }
`;

const OtherVersionToast = ({ onClick, buttonText, text }) => (
  <Flex alignItems="center" flexWrap="wrap">
    <Text color="nav.100">{text}</Text>
    <Button buttonStyle="tertiary" onClick={onClick} mt={1}>
      {buttonText}
    </Button>
  </Flex>
);

const ViewPolicy = props => {
  const {
    documentView,
    viewer,
    tenant,
    router,
    location,
    route,
    hasPolicyActionComment,
    PolicyActionCommentSection,
    isInitiallyExpandToc = false,
    displayConfig = {},
    actionDocumentPk
  } = props;

  const {
    document,
    documentAccessTokens,
    guestAccessLink,
    showChanges,
    canCreateReadReceipt,
    canEditDocument,
    showTimeline,
    lippincottEnabled,
    unsubscriptionEnabled,
    documentSave
  } = documentView;
  const { pk } = document;

  const policyActions = props.policyActions || documentView.policyActions;
  const [displayChangesDiff, setDisplayChangesDiff] = useState(false);
  const [tocExpanded, setTocExpanded] = useState(isInitiallyExpandToc);
  const [applicabilityExpanded, setApplicabilityExpanded] = useState(false);
  const [acknowlegmentExpanded, setAcknowlegmentExpanded] = useState(false);
  const [showChangesTaskId, setShowChangesTaskId] = useState(
    documentView.showChangesTaskId
  );
  const getInitApprovable = () => documentView?.document.approvable !== "NO";
  const [initApprovable, setInitApprovable] = useState(getInitApprovable);
  const [activeTOCHeader, setActiveTOCHeader] = useState(null);

  // the document state for the first action in the selected timeline range
  const [timelineDocument, setTimelineDocument] = useState(null);

  /*
    The next state blocks showing the "already approved page"
    when approval is already being processed
  */
  const [isApprovalInProgress, setIsApprovalInProgress] = useState(false);
  const [includePastApprovals, setIncludePastApprovals] = useState(false);

  // the effect runs after the render,
  // so previousDocumentPk will still have the previous pk
  // when compared to document.pk below
  const previousDocumentPk = useRef();
  useEffect(() => {
    previousDocumentPk.current = document.pk;
  });
  if (
    previousDocumentPk.current &&
    previousDocumentPk.current !== document.pk
  ) {
    // reset the timeline document when the user navigates to another document,
    // otherwise the page will still show this timelineDocument instead of the new one
    // because this component doesn't unmount and the state doesn't get reset on its own
    setTimelineDocument(null);
    previousDocumentPk.current = document.pk;
  }

  const diff = useRef(null);

  const showChangesTaskResult = useTaskResult(showChangesTaskId);
  const {
    loading: taskLoading,
    error: taskError,
    result
  } = showChangesTaskResult;
  const numRegsPropertyDisplay = 3;

  if (!taskLoading && !taskError && result) {
    diff.current = result.diff;
  }

  const toggleTableOfContents = () => {
    setTocExpanded(!tocExpanded);
  };
  const closeTableOfContents = () => {
    setTocExpanded(false);
  };
  const handleDisplayChangesDiff = () => {
    setDisplayChangesDiff(!displayChangesDiff);
  };

  const showMsg = (response, errors, msgId) => {
    const { t } = props;
    if (errors) {
      errors.forEach(error => {
        createErrorMessage(error);
      });
    } else {
      createSuccessMessage(t(msgId));
    }
  };

  const assignAcknowledgmentMsg = (response, errors) => {
    showMsg(response, errors, "acknowledgments.assign.successMessage");
  };

  const deleteAcknowledgmentMsg = (response, errors) => {
    showMsg(response, errors, "acknowledgments.assign.successMessage");
  };

  const handleTimelineChangeTask = (initialDocument, taskId) => {
    setTimelineDocument(initialDocument);
    setShowChangesTaskId(taskId);
  };

  useEffect(() => {
    const { t } = props;
    const document = props.documentView.document;
    const documentLabel = props.labels.documentLabel.toLowerCase();
    if (document.draftVersionLink) {
      createInfoMessage(
        () => (
          <OtherVersionToast
            onClick={() => props.router.push(document.draftVersionLink)}
            buttonText={t("documentControl.view.toasts.draftButton")}
            text={t("documentControl.view.toasts.draftText", { documentLabel })}
          />
        ),
        { autoClose: false, toastId: "draft-version-toast" }
      );
    } else if (document.pendingVersionLink) {
      createInfoMessage(
        () => (
          <OtherVersionToast
            onClick={() => props.router.push(document.pendingVersionLink)}
            buttonText={t("documentControl.view.toasts.pendingButton")}
            text={t("documentControl.view.toasts.pendingText")}
          />
        ),
        { autoClose: false, toastId: "pending-version-toast" }
      );
    } else if (document.scheduledVersionLink) {
      createInfoMessage(
        () => (
          <OtherVersionToast
            onClick={() => props.router.push(document.scheduledVersionLink)}
            buttonText={t("documentControl.view.toasts.scheduledButton")}
            text={t("documentControl.view.toasts.scheduledText")}
          />
        ),
        { autoClose: false, toastId: "scheduled-version-toast" }
      );
    }
  }, [props]);

  const isViewRoute = route?.data?.routeName === "document_view";
  let showLegacyEffectiveDate;
  // Directly compare with false instead of !tenant.hasScheduledEffectiveDateEnabled, cause it might be undefined
  // And, in such case, !tenant.hasScheduledEffectiveDateEnabled would be evaluated as true
  // This if else might be simplified to 1 row. But it would be not readable :(
  if (
    tenant.hasScheduledEffectiveDateEnabled === true ||
    typeof tenant.hasScheduledEffectiveDateEnabled === "undefined"
  ) {
    showLegacyEffectiveDate = false;
  } else {
    showLegacyEffectiveDate = true;
  }

  const isApprovalRoute = route?.data?.routeName === "document_approve";
  const isApprovalPage = isApprovalRoute && document.approvable !== "NO";
  const showDocumentAlreadyApprovedMessage =
    isApprovalRoute && !initApprovable && !isApprovalInProgress;

  const documentToDisplay = timelineDocument || document;

  const {
    showPrintAndShare = true,
    showLippincottPanel = true,
    showApprovalSignaturePanel = true,
    showApplicabilityPanel = true,
    showHistoryPanel = true,
    showCommentsPanel = true,
    showApprovalFlow = false,
    showCloneAction = true
  } = displayConfig;

  let { showAcknowledgementsPanel = true } = displayConfig;

  const showLippincottSearch =
    showLippincottPanel && document.isLippincottDocument && lippincottEnabled;

  // Ack Panel depends on a status of a document:
  // show a panel in a viewing page for active/scheduled document.
  if (showAcknowledgementsPanel) {
    if (
      !isViewRoute ||
      (!isActive(document.status) && !isScheduled(document.status))
    ) {
      showAcknowledgementsPanel = false;
    }
  }

  const lippincottSearchHandle = searchQuery => {
    const to = {
      pathname: `${getRootUrl()}/integrations/lippincott/search`,
      query: { q: searchQuery }
    };
    router.push(to);
  };

  const [pageHasReachedTheEnd, setPageHasReachedTheEnd] = useState(false);

  const checkIfReachedEnd = () => {
    const finalContentElement = window.document.getElementById(
      "end-of-policy-content"
    );
    const hasReachedEnd =
      finalContentElement &&
      finalContentElement.getBoundingClientRect().bottom <= window.innerHeight;

    setPageHasReachedTheEnd(prevValue =>
      prevValue ? prevValue : hasReachedEnd
    );
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfReachedEnd);

    return () => {
      window.removeEventListener("scroll", checkIfReachedEnd);
    };
  });

  const hasMoreStandardsAndRegulations = () => {
    return (
      documentToDisplay.regulations &&
      documentToDisplay.regulations.length > numRegsPropertyDisplay
    );
  };

  const togglePastApproval = () =>
    setIncludePastApprovals(!includePastApprovals);

  return (
    <Fragment>
      {showDocumentAlreadyApprovedMessage ? (
        <DocumentApprovedMessage policyUrl={`${getRootUrl()}/policy/${pk}`} />
      ) : (
        <Fragment>
          <SecondaryNav
            isApprovalPage={isApprovalPage}
            document={document}
            documentSave={documentSave}
            showChangesToggle={showChanges && showChangesTaskId !== null} // want this to be true if empty string
            showChangesTaskResult={showChangesTaskResult}
            displayChangesDiff={displayChangesDiff}
            handleDisplayChangesDiff={handleDisplayChangesDiff}
            contentContainerMarginLeft={tocExpanded ? "320px" : "auto"}
            policyActions={policyActions}
            route={route}
            pageHasReachedTheEnd={pageHasReachedTheEnd}
            showCloneAction={showCloneAction}
            setShouldBlockAlreadyApprovedPage={setIsApprovalInProgress}
          >
            <ContentContainer id="view-content" maxWidth={992}>
              {hasPolicyActionComment ? (
                <Box width={1} px={[0, 0, 3]} mt={6}>
                  <PolicyActionCommentSection
                    documentName={document.name}
                    documentPk={actionDocumentPk || pk}
                    location={location}
                    router={router}
                  />
                </Box>
              ) : null}
              <StatusBar
                document={document}
                timelineDocument={timelineDocument}
                documentAccessTokens={documentAccessTokens}
                guestAccessLink={guestAccessLink}
                viewer={viewer}
                applicabilityExpanded={applicabilityExpanded}
                isApprovalPage={isApprovalPage}
                policyActions={policyActions}
                canEditDocument={canEditDocument}
                showPrintAndShare={showPrintAndShare}
                showWorkflowName={showApprovalFlow}
                displayChangesDiff={displayChangesDiff}
                includePastApprovals={includePastApprovals}
              />
              <Media query={{ minWidth: theme.breakpoints[1] }}>
                {matches =>
                  matches && (
                    <Box width={1} pt={0} pb={3} px={3}>
                      <PolicyProperties
                        approvalDate={documentToDisplay.approvalDate}
                        effectiveDate={documentToDisplay.effectiveDate}
                        revisionDate={documentToDisplay.revisionDate}
                        expirationDate={documentToDisplay.expirationDate}
                        originationDate={
                          showLegacyEffectiveDate
                            ? documentToDisplay.legacyEffectiveDate
                            : documentToDisplay.originationDate
                        }
                        author={documentToDisplay.author}
                        category={documentToDisplay.category}
                        bloc={documentToDisplay.bloc}
                        tags={documentToDisplay.tags}
                        headerImageUrl={documentToDisplay.headerImageUrl}
                        regulations={documentToDisplay.regulations}
                        numRegulationsDisplay={numRegsPropertyDisplay}
                      />
                    </Box>
                  )
                }
              </Media>

              <Box width={1} pt={0} pb={[0, 0, 6]} px={[0, 0, 3]}>
                <PolicyContent
                  name={documentToDisplay.name}
                  html={
                    displayChangesDiff ? diff.current : documentToDisplay.html
                  }
                  tableOfContentsCallback={setActiveTOCHeader}
                  isRestricted={documentToDisplay.restricted}
                />
                <div id="end-of-policy-content" />
              </Box>
              {document.attachments.length > 0 && (
                <Box width={1} px={[0, 0, 3]}>
                  <AttachmentsPanel document={documentToDisplay} />
                </Box>
              )}
              {showLippincottSearch && (
                <Box width={1} px={[0, 0, 3]}>
                  <LippincottSearchPanel onSearch={lippincottSearchHandle} />
                </Box>
              )}
              {showApprovalSignaturePanel && (
                <Box width={1} px={[0, 0, 3]}>
                  <ApprovalSignatureSection
                    document={documentToDisplay}
                    isOpenPastApproval={includePastApprovals}
                    togglePastApproval={togglePastApproval}
                    showStepDescription={tenant.documentSettings?.showCommittee}
                  />
                </Box>
              )}
              {showApplicabilityPanel && tenant.showApplicability && (
                <Media query={{ minWidth: theme.breakpoints[1] }}>
                  {matches =>
                    matches && (
                      <Box width={1} px={[0, 0, 3]}>
                        <ApplicabilityPanel
                          document={documentToDisplay}
                          expanded={applicabilityExpanded}
                          onExpandedChanged={setApplicabilityExpanded}
                        />
                      </Box>
                    )
                  }
                </Media>
              )}
              <Media query={{ minWidth: theme.breakpoints[1] }}>
                {isDesktop =>
                  isDesktop &&
                  hasMoreStandardsAndRegulations() && (
                    <Box width={1} px={[0, 0, 3]}>
                      <StandardsAndRegulationsPanel
                        regulations={documentToDisplay.regulations}
                      />
                    </Box>
                  )
                }
              </Media>
              {!viewer.isGuest && (
                <Fragment>
                  {showAcknowledgementsPanel && canCreateReadReceipt && (
                    <Box width={1} px={[0, 0, 3]}>
                      <AcknowledgmentsPanel
                        onAssignMsg={assignAcknowledgmentMsg}
                        onDeleteMsg={deleteAcknowledgmentMsg}
                        documentId={documentView?.document?.pk}
                        linePk={documentView?.document?.linePk}
                        expanded={acknowlegmentExpanded}
                        onExpandedChanged={setAcknowlegmentExpanded}
                      />
                    </Box>
                  )}
                  {isOptionalApproval(document) && (
                    <Box width={1} px={[0, 0, 3]}>
                      <OptionalApproval document={document} />
                    </Box>
                  )}
                  {showHistoryPanel && (
                    <Box width={1} px={[0, 0, 3]}>
                      <HistoryCard
                        document={document}
                        actions={document.actions}
                        documentPk={pk}
                        viewer={viewer}
                      />
                    </Box>
                  )}
                  {showCommentsPanel && (
                    <Box width={1} px={[0, 0, 3]}>
                      <CommentPanel
                        document={document}
                        isEditPage={false}
                        unsubscriptionEnabled={unsubscriptionEnabled}
                        documentName={document.name}
                      />
                    </Box>
                  )}
                </Fragment>
              )}
              <TableOfContents
                countAttachments={documentToDisplay.attachments.length}
                html={documentToDisplay.html}
                expanded={tocExpanded}
                activeItem={activeTOCHeader}
                onToggle={toggleTableOfContents}
                onClose={closeTableOfContents}
              />
              {showTimeline && (
                <DocumentTimeline
                  documentPk={document.pk}
                  onTimelineChangesTaskChange={handleTimelineChangeTask}
                />
              )}
            </ContentContainer>
            <ShowChangesLoadingModal isOpen={taskLoading} />
          </SecondaryNav>
        </Fragment>
      )}
    </Fragment>
  );
};

const ViewPolicyContainer = (props, ...rest) => {
  return (
    <ViewPolicyStyledContainer>
      <Media query={{ minWidth: theme.breakpoints[1] }}>
        {isDesktop => (
          <ViewPolicy {...props} {...rest} isInitiallyExpandToc={isDesktop} />
        )}
      </Media>
    </ViewPolicyStyledContainer>
  );
};

export default withTranslation()(
  withLabelContext(withViewer(withTenantContext(ViewPolicyContainer)))
);
